import { createContext, useEffect, useState, ReactNode } from 'react'
import { User, apiService } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { isDemoMode, getDemoRole, DEMO_TOKEN } from '../demo/demoMode'
import { buildDemoUser } from '../demo/demoData'

export interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  loading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Persisted alongside the auth token so a page reload restores the session
// (the backend exposes no token-introspection / "current user" endpoint).
const USER_STORAGE_KEY = 'ecombine_user'

/**
 * Fetch the current user's permission scopes and push them into the auth store.
 *
 * Scope population is deliberately non-fatal: a user who cannot load scopes
 * still gets a session, and scope-gated features fail closed. But the failure
 * must be *visible*. Previously this was a bare `catch` that logged a generic
 * warning with no error object, which is how a plain response-shape mismatch
 * (`{ scopes: [...] }` read as a bare array) stayed live and undetected — the
 * `.map` TypeError was indistinguishable from a network blip (#154).
 *
 * So: validate the envelope explicitly and throw a message that names the
 * contract, and log the caught error itself via `console.error` (the
 * convention used elsewhere in this file) rather than a bare warning.
 */
async function loadUserScopes(): Promise<void> {
  try {
    const response = await apiService.getMyScopes()

    if (response.error) {
      throw new Error(response.error)
    }

    // No envelope at all — nothing to populate, and not an error worth throwing
    // (the request may have been intercepted or returned an empty body).
    if (!response.data) {
      return
    }

    const { scopes } = response.data
    if (!Array.isArray(scopes)) {
      throw new TypeError(
        `GET /scopes/my-scopes returned an unexpected shape: expected { scopes: [...] }, got ${JSON.stringify(
          response.data
        )}`
      )
    }

    // Element-level check, not just the envelope: if `scopeType` were renamed
    // server-side, every entry would become `{ scopeType: undefined }` and
    // `hasScope` would return false forever — #154 one level down, and just as
    // silent. A scope with no type cannot gate anything, so treat it as the
    // same contract violation.
    const malformed = scopes.find(s => typeof s?.scopeType !== 'string')
    if (malformed !== undefined) {
      throw new TypeError(
        `GET /scopes/my-scopes returned a scope without a string scopeType: ${JSON.stringify(
          malformed
        )}`
      )
    }

    useAuthStore
      .getState()
      .setScopes(scopes.map(s => ({ scopeType: s.scopeType, scopeId: s.scopeId ?? null })))
  } catch (error) {
    // Non-fatal for the session, but never silent: a programming error here
    // (wrong shape, renamed field) must be greppable in the console.
    console.error('Failed to fetch user scopes:', error)
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user && !!token

  useEffect(() => {
    // Demo mode: boot straight into the dashboard as the selected demo role,
    // no backend round-trip required.
    if (isDemoMode()) {
      const demoUser = buildDemoUser(getDemoRole())
      setUser(demoUser)
      setToken(DEMO_TOKEN)
      apiService.setToken(DEMO_TOKEN)
      // Populate role-appropriate scopes (e.g. station mode for organizers).
      void loadUserScopes()
      setLoading(false)
      return
    }

    // Restore an existing session on app start. The backend has no token-
    // introspection endpoint, so we persist the user alongside the token at
    // login and rehydrate both here. A stale/expired token self-heals: the API
    // service clears it and redirects to /login on the first 401 response.
    const savedToken = apiService.getToken()
    const savedUser = localStorage.getItem(USER_STORAGE_KEY)
    // A real session requires a non-demo token AND a persisted user. (A leftover
    // DEMO_TOKEN after exiting demo, or a token with no user, is not a real
    // session — clear it so we don't half-initialize or restore a stale user.)
    if (savedToken && savedToken !== DEMO_TOKEN && savedUser) {
      try {
        setUser(JSON.parse(savedUser) as User)
        setToken(savedToken)
      } catch {
        apiService.clearToken()
        localStorage.removeItem(USER_STORAGE_KEY)
      }
    } else if (savedToken || savedUser) {
      apiService.clearToken()
      localStorage.removeItem(USER_STORAGE_KEY)
    }
    setLoading(false)
  }, [])

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true)
      const response = await apiService.login({ email, password })

      if (response.data) {
        const { user, token: authToken, refreshToken } = response.data
        setUser(user)
        setToken(authToken)
        apiService.setToken(authToken)
        if (refreshToken) {
          apiService.setRefreshToken(refreshToken)
        }
        // Persist only what the UI needs to rehydrate a session — avoid storing
        // sensitive profile fields (DOB, phone) in localStorage.
        const persistedUser: User = {
          id: user.id,
          email: user.email,
          role: user.role,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          profile: user.profile
            ? {
                id: user.profile.id,
                firstName: user.profile.firstName,
                lastName: user.profile.lastName,
              }
            : undefined,
        }
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(persistedUser))

        // Fetch user scopes after successful login. Non-fatal, but errors are
        // logged rather than swallowed — see loadUserScopes.
        await loadUserScopes()

        return { success: true }
      } else {
        return {
          success: false,
          error: response.message || response.error || 'Login failed',
        }
      }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        error: 'Unable to connect to server',
      }
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setToken(null)
      apiService.clearToken()
      localStorage.removeItem(USER_STORAGE_KEY)
    }
  }

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export { AuthContext }
export default AuthContext
